import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Calendar, Info } from 'lucide-react';
import { useClientDetailsQuery } from '../../services/hooks/useClientes';
import { useActualizarEstadoEsperaMutation } from '../../services/hooks/usePlazas';
import { useUser } from '@clerk/clerk-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { ResumenClasesCompletas } from './ResumenClasesCompletas';
import type { ClaseCompletaResultado } from '../../services/api/plazaService';

interface ModalEsperaPlazasProps {
    clientId: string;
    onClose: () => void;
}

const ModalEsperaPlazas: React.FC<ModalEsperaPlazasProps> = ({ clientId, onClose }) => {
    // Obtener datos del cliente
    const { data: cliente, isLoading } = useClientDetailsQuery(clientId);
    
    // Estado del formulario
    const [enEspera, setEnEspera] = useState(false);
    const [fechaInicio, setFechaInicio] = useState<string>('');
    const [fechaFin, setFechaFin] = useState<string>('');
    const [motivo, setMotivo] = useState('');
    // P4 — clases en las que no cupo al reactivar. Mientras tenga valor, el modal
    // enseña el resumen en vez del formulario.
    const [resumenReactivacion, setResumenReactivacion] = useState<ClaseCompletaResultado[] | null>(null);
    
    // Datos del usuario actual
    const { user } = useUser();
    const userName = user?.firstName || user?.username || 'Usuario del sistema';
    
    // Hook para actualizar el estado de espera
    const actualizarEsperaMutation = useActualizarEstadoEsperaMutation();
    
    // Inicializar estados cuando los datos del cliente están disponibles
    useEffect(() => {
        if (cliente) {
            setEnEspera(cliente.enEspera || false);
            
            // Formatear fechas si existen
            if (cliente.fechaInicioEspera) {
                const fechaInicio = new Date(cliente.fechaInicioEspera);
                setFechaInicio(format(fechaInicio, 'yyyy-MM-dd'));
            }
            
            if (cliente.fechaFinEspera) {
                const fechaFin = new Date(cliente.fechaFinEspera);
                setFechaFin(format(fechaFin, 'yyyy-MM-dd'));
            }
            
            setMotivo(cliente.motivoEspera || '');
        }
    }, [cliente]);
    
    // Manejar envío del formulario
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validar que tengamos toda la información necesaria
        if (enEspera && (!fechaInicio || !fechaFin || !motivo)) {
            return; // El formulario HTML también valida, pero por seguridad
        }
        
        // Preparar datos para la mutación
        const datos = {
            clienteId: clientId,
            enEspera,
            fechaInicioEspera: enEspera ? new Date(fechaInicio) : null,
            fechaFinEspera: enEspera ? new Date(fechaFin) : null,
            motivoEspera: enEspera ? motivo : null,
            updatedBy: userName
        };
        
        // Ejecutar la mutación
        actualizarEsperaMutation.mutate(datos, {
            onSuccess: (data) => {
                // P4 — al reactivar puede que no haya cupido en todas sus clases
                // (se llenaron mientras estaba fuera). Eso no se puede contar en un
                // toast que se va: el modal se queda abierto con el detalle, clase
                // por clase, y lo cierra el operador.
                const completas = data.reactivacion?.clasesCompletas ?? [];
                if (completas.length) {
                    setResumenReactivacion(completas);
                    return;
                }
                onClose(); // Cerrar el modal al terminar con éxito
            }
        });
    };
    
    // Formatear fecha para mostrar
    const formatFecha = (fechaStr: string | null | undefined): string => {
        if (!fechaStr) return 'No establecida';
        try {
            const fecha = new Date(fechaStr);
            return format(fecha, 'd MMMM yyyy', { locale: es });
        } catch (error) {
            return 'Fecha inválida';
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Gestionar Estado de Espera</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={20} />
                    </button>
                </div>
                
                {resumenReactivacion ? (
                    /* P4 — reactivado, pero no cupo en todo. Mismo componente que al
                       dar una plaza, para que el operador lea lo mismo en los dos
                       sitios. */
                    <div className="space-y-4">
                        <p className="text-sm text-gray-700">
                            El cliente está reactivado y ha vuelto a entrar en sus clases, salvo en
                            las que se llenaron mientras estaba en espera:
                        </p>
                        <ResumenClasesCompletas clases={resumenReactivacion} />
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                        >
                            Entendido
                        </button>
                    </div>
                ) : isLoading ? (
                    <div className="flex justify-center py-8">
                        <Loader2 size={32} className="animate-spin text-blue-500" />
                    </div>
                ) : (
                    <>
                        {/* Información del estado actual */}
                        <div className={`p-4 mb-4 rounded-md ${cliente?.enEspera ? 'bg-yellow-50' : 'bg-green-50'}`}>
                            <div className="flex items-start">
                                <Info size={20} className={`mr-2 ${cliente?.enEspera ? 'text-yellow-600' : 'text-green-600'}`} />
                                <div>
                                    <h3 className="font-medium mb-1">Estado actual:</h3>
                                    <p className="text-sm">
                                        {cliente?.enEspera 
                                            ? `En espera desde ${formatFecha(cliente?.fechaInicioEspera)} hasta ${formatFecha(cliente?.fechaFinEspera)}`
                                            : 'Cliente activo (no está en espera)'}
                                    </p>
                                    {cliente?.enEspera && cliente?.motivoEspera && (
                                        <p className="text-sm mt-1">
                                            <span className="font-medium">Motivo:</span> {cliente.motivoEspera}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="flex items-center mb-4">
                                <input
                                    type="checkbox"
                                    id="enEspera"
                                    checked={enEspera}
                                    onChange={(e) => setEnEspera(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="enEspera" className="ml-2 block text-sm font-medium text-gray-700">
                                    {enEspera ? 'Reactivar cliente' : 'Poner en espera'}
                                </label>
                            </div>
                            
                            {enEspera && (
                                <div className="p-4 border border-blue-100 rounded-md bg-blue-50">
                                    {/* P4 — este aviso decía lo contrario ("mantendrá su
                                        posición en las clases"), que es justo lo que ha
                                        dejado de ser verdad: la pausa ahora libera el
                                        sitio. Es lo primero que tiene que saber quien
                                        pausa, así que va en ámbar y antes de las fechas. */}
                                    <div className="text-sm mb-4 flex items-start text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                                        <AlertCircle size={16} className="mr-2 mt-0.5 shrink-0" />
                                        <p>
                                            Al poner el cliente en espera, <strong>su sitio en las clases de
                                            esas fechas queda libre</strong> y puede ocuparlo otra persona:
                                            se le saca de las clases del periodo y la plaza pasa a estar
                                            disponible. Al reactivarlo volverá a entrar en las que todavía
                                            tengan hueco, y por las que estén completas se le podrá dar una
                                            recuperación.
                                        </p>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha de inicio <span className="text-red-600">*</span>
                                            </label>
                                            <div className="flex items-center border rounded-md overflow-hidden">
                                                <span className="px-3 py-2 bg-gray-100">
                                                    <Calendar size={16} className="text-gray-500" />
                                                </span>
                                                <input
                                                    type="date"
                                                    required={enEspera}
                                                    value={fechaInicio}
                                                    onChange={(e) => setFechaInicio(e.target.value)}
                                                    className="flex-1 p-2 outline-none text-sm"
                                                    min={new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Fecha de fin <span className="text-red-600">*</span>
                                            </label>
                                            <div className="flex items-center border rounded-md overflow-hidden">
                                                <span className="px-3 py-2 bg-gray-100">
                                                    <Calendar size={16} className="text-gray-500" />
                                                </span>
                                                <input
                                                    type="date"
                                                    required={enEspera}
                                                    value={fechaFin}
                                                    onChange={(e) => setFechaFin(e.target.value)}
                                                    className="flex-1 p-2 outline-none text-sm"
                                                    min={fechaInicio || new Date().toISOString().split('T')[0]}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Motivo <span className="text-red-600">*</span>
                                            </label>
                                            <textarea
                                                required={enEspera}
                                                value={motivo}
                                                onChange={(e) => setMotivo(e.target.value)}
                                                rows={3}
                                                className="w-full p-2 border rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                                                placeholder="Indique el motivo de la espera..."
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex justify-end space-x-2 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={actualizarEsperaMutation.isPending}
                                    className={`px-4 py-2 rounded-md text-white flex items-center ${
                                        enEspera ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'
                                    }`}
                                >
                                    {actualizarEsperaMutation.isPending && (
                                        <Loader2 size={16} className="animate-spin mr-2" />
                                    )}
                                    {enEspera ? 'Poner en espera' : 'Reactivar cliente'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default ModalEsperaPlazas;